import React, { useState, useRef, useCallback } from 'react';
import { Autocomplete, TextField, Box, Avatar, Typography, CircularProgress } from '@mui/material';
import api from '../api';
import { getThumbFromItem } from '../utils/photoUrl';

/**
 * Reusable object search/select component.
 * Returns { id, name } on selection.
 */
export default function ObjectSelect({ value, onChange, label = 'Objekt auswählen', fullWidth = true, sx }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const searchTimeout = useRef(null);

  const searchObjects = useCallback(async (query) => {
    if (!query || query.length < 2) { setOptions([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/objects/search?q=${encodeURIComponent(query)}`);
      setOptions(res.data);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Parse stored JSON value to display object
  const selectedObj = React.useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object' && value.id) return value;
    try {
      const parsed = JSON.parse(value);
      return parsed && parsed.id ? parsed : null;
    } catch {
      return null;
    }
  }, [value]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(opt) => opt.name || ''}
      value={selectedObj}
      onChange={(_, newVal) => {
        if (newVal) {
          onChange({ id: newVal._id || newVal.id, name: newVal.name });
        } else {
          onChange(null);
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, val) => {
        setInputValue(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => searchObjects(val), 300);
      }}
      filterOptions={(opts) => opts}
      loading={loading}
      noOptionsText="Objekt suchen..."
      isOptionEqualToValue={(opt, val) => (opt._id || opt.id) === (val._id || val.id)}
      fullWidth={fullWidth}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Name eingeben..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={(props, opt) => (
        <Box component="li" {...props} key={opt._id || opt.id}>
          <Avatar
            src={opt.photoUrl ? getThumbFromItem(opt, 64) : undefined}
            sx={{ mr: 1.5, width: 28, height: 28 }}
          >
            {opt.name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2">{opt.name}</Typography>
            {opt.description && (
              <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
            )}
          </Box>
        </Box>
      )}
    />
  );
}
